import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Inbox, FolderPlus, MoreHorizontal, Pencil, Trash2, Loader2,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const COLOR_MAP = {
  zinc: "bg-[#F5F5F5] text-[#888888]",
  blue: "bg-blue-100 text-blue-600",
  emerald: "bg-emerald-100 text-emerald-600",
  amber: "bg-amber-100 text-amber-600",
  red: "bg-red-100 text-red-600",
  violet: "bg-violet-100 text-violet-600",
  cyan: "bg-cyan-100 text-cyan-600",
  rose: "bg-rose-100 text-rose-600",
};

const COLOR_DOT = {
  zinc: "bg-zinc-400", blue: "bg-blue-500", emerald: "bg-emerald-500",
  amber: "bg-amber-500", red: "bg-red-500", violet: "bg-violet-500",
  cyan: "bg-cyan-500", rose: "bg-rose-500",
};

const COLORS = Object.keys(COLOR_MAP);

export default function InboxGroups({ activeGroup, onGroupChange, totalAliases, refreshKey }) {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState("");
  const [newColor, setNewColor] = useState("blue");
  const [creating, setCreating] = useState(false);
  const [editGroup, setEditGroup] = useState(null);
  const [editName, setEditName] = useState("");

  const fetchGroups = useCallback(async () => {
    try {
      const { data } = await api.get("/inbox-groups");
      setGroups(data.groups || []);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchGroups(); }, [fetchGroups, refreshKey]);

  const handleCreate = async () => {
    if (!newName.trim()) return;
    setCreating(true);
    try {
      await api.post("/inbox-groups", { name: newName.trim(), color: newColor });
      setNewName("");
      setShowCreate(false);
      await fetchGroups();
      toast.success("Group created");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to create group");
    } finally {
      setCreating(false);
    }
  };

  const handleRename = async () => {
    if (!editGroup || !editName.trim()) return;
    try {
      await api.put(`/inbox-groups/${editGroup.group_id}`, { name: editName.trim() });
      setEditGroup(null);
      await fetchGroups();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to rename");
    }
  };

  const handleDelete = async (groupId) => {
    try {
      await api.delete(`/inbox-groups/${groupId}`);
      if (activeGroup === groupId) onGroupChange(null);
      await fetchGroups();
      toast.success("Group deleted");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to delete");
    }
  };

  if (loading) return null;

  const ungroupedCount = totalAliases - groups.reduce((sum, g) => sum + (g.alias_count || 0), 0);

  return (
    <div className="space-y-1" data-testid="inbox-groups">
      {/* All */}
      <button
        onClick={() => onGroupChange(null)}
        className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-colors ${!activeGroup ? "bg-[#F5F5F5] text-[#111111] font-medium" : "text-zinc-500 hover:bg-[#F5F5F5] hover:text-[#555555]"}`}
        data-testid="group-all"
      >
        <Inbox size={15} />
        <span className="flex-1 text-left">All Inboxes</span>
        <span className="text-[10px] text-zinc-500 tabular-nums">{totalAliases}</span>
      </button>

      {/* Groups */}
      {groups.map((g) => (
        <div key={g.group_id} className="flex items-center group" data-testid={`group-${g.group_id}`}>
          <button
            onClick={() => onGroupChange(g.group_id)}
            className={`flex-1 flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-colors ${activeGroup === g.group_id ? "bg-[#F5F5F5] text-[#111111] font-medium" : "text-zinc-500 hover:bg-[#F5F5F5] hover:text-[#555555]"}`}
          >
            <div className={`w-2 h-2 rounded-full ${COLOR_DOT[g.color] || COLOR_DOT.zinc}`} />
            <span className="flex-1 text-left truncate">{g.name}</span>
            <span className="text-[10px] text-zinc-500 tabular-nums">{g.alias_count || 0}</span>
          </button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="p-1 opacity-0 group-hover:opacity-100 transition-opacity text-zinc-500 hover:text-[#888888]">
                <MoreHorizontal size={13} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-32">
              <DropdownMenuItem onClick={() => { setEditGroup(g); setEditName(g.name); }}>
                <Pencil size={12} className="mr-2" /> Rename
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleDelete(g.group_id)} className="text-red-600">
                <Trash2 size={12} className="mr-2" /> Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      ))}

      {/* Create */}
      {!showCreate ? (
        <button
          onClick={() => setShowCreate(true)}
          className="w-full flex items-center gap-2 px-3 py-2 text-xs text-zinc-500 hover:text-[#888888] transition-colors"
          data-testid="create-group-btn"
        >
          <FolderPlus size={13} /> New group
        </button>
      ) : (
        <div className="px-2 py-2 space-y-2">
          <Input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="Group name"
            className="h-8 text-xs"
            autoFocus
            onKeyDown={(e) => e.key === "Enter" && handleCreate()}
            data-testid="group-name-input"
          />
          <div className="flex gap-1">
            {COLORS.map((c) => (
              <button
                key={c}
                onClick={() => setNewColor(c)}
                className={`w-5 h-5 rounded-full ${COLOR_DOT[c]} ${newColor === c ? "ring-2 ring-offset-1 ring-zinc-400" : ""}`}
              />
            ))}
          </div>
          <div className="flex gap-1.5">
            <Button variant="outline" size="sm" onClick={() => setShowCreate(false)} className="flex-1 h-7 text-[10px]">Cancel</Button>
            <Button size="sm" onClick={handleCreate} disabled={creating || !newName.trim()} className="flex-1 h-7 text-[10px] bg-zinc-900 text-white" data-testid="group-create-submit">
              {creating ? <Loader2 size={12} className="animate-spin" /> : "Create"}
            </Button>
          </div>
        </div>
      )}

      {/* Rename Dialog */}
      <Dialog open={!!editGroup} onOpenChange={(v) => { if (!v) setEditGroup(null); }}>
        <DialogContent className="sm:max-w-[320px]" data-testid="rename-group-dialog">
          <DialogHeader>
            <DialogTitle className="text-sm">Rename group</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Input
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              className="h-9 text-sm"
              autoFocus
              onKeyDown={(e) => e.key === "Enter" && handleRename()}
              data-testid="rename-group-input"
            />
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setEditGroup(null)} className="flex-1 text-xs">Cancel</Button>
              <Button onClick={handleRename} disabled={!editName.trim()} className="flex-1 bg-zinc-900 text-white text-xs" data-testid="rename-group-save">Save</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
